import { useIsChuseokCallOff } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import {
  call,
  ColorIcon,
  colors,
  customEvent,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  buildCrematoriumPageSchema,
  CrematoriumRegionFAQ,
  GOI_WEB_URL,
  RichSearch,
  HLine,
  SidoName,
} from "goi_common";

import { Box, Button, HStack, VStack } from "@chakra-ui/react";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";
import { GetStaticPaths, GetStaticProps } from "next";

type CrematoriumDetail = (typeof crematoriums)[number];

import Head from "next/head";

const { targetSidos, regionColor } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const sidoList = Array.from(FUNERAL_SIDO.entries()).filter(([code]) =>
    targetSidos.includes(code.toString())
  );

  const paths = sidoList
    .map((sido) => {
      const sigungus = FUNERAL_SIGUNGU.get(sido[0]);

      if (sigungus) {
        return Array.from(sigungus.values()).map((sigungu) => {
          return {
            params: {
              sido: sido[1],
              sigungu,
            },
          };
        });
      }

      return [];
    })
    .filter((i) => i != null);

  return {
    paths: paths.flat(),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { sido, sigungu } = params as { sido: string; sigungu: string };

  const sidoCode = Array.from(FUNERAL_SIDO.entries()).find(
    ([, value]) => value === SidoName[sido as keyof typeof SidoName]
  )?.[0];

  const sigunguCode = Array.from(FUNERAL_SIGUNGU.values())
    .flatMap((m) => Array.from(m.entries()))
    .find(([, name]) => name === sigungu)?.[0];

  /** 시군구까지 일치하는 것을 앞으로 배치, 시도일치를 뒤로 배치 */
  const regionCrematoriums = crematoriums
    .filter((c) => targetSidos.includes(c.sido.toString()))
    .sort((a, b) => {
      // 시도 일치 여부 비교 (1순위)
      const sidoCompare =
        Number(b.sido === Number(sidoCode)) -
        Number(a.sido === Number(sidoCode));

      if (sidoCompare !== 0) return sidoCompare;

      // 시군구 일치 여부 비교 (2순위)
      return (
        Number(b.sigungu === Number(sigunguCode)) -
        Number(a.sigungu === Number(sigunguCode))
      );
    });

  const district =
    sigungu.length >= 3 && (sigungu.endsWith("시") || sigungu.endsWith("구"))
      ? sigungu.slice(0, -1)
      : sigungu;

  const regionText = `${sido} ${district}`;
  const firstCrematorium = regionCrematoriums?.[0];
  const secondCrematorium = regionCrematoriums?.[1];

  const title = `(${regionText}) 화장장, 화장터 시설 비용 및 예약 :: 충청대전세종장례소개포털`;
  const description = `(${regionText}) 화장장, 화장터 시설 비용 및 예약 정보를 충청대전세종장례소개포털에서 알아보세요. 화장장은 빠른 예약이 가장 중요합니다. ${
    firstCrematorium
      ? `${firstCrematorium.companyname}, ${firstCrematorium.address}`
      : ""
  } ${
    secondCrematorium
      ? `${secondCrematorium.companyname}, ${secondCrematorium.address}`
      : ""
  }`;

  const keywords = `${regionText} 화장장, ${sido} 화장장, ${sigungu} 화장장, ${district} 화장장, 
  ${regionText} 화장터, ${sido} 화장터, ${sigungu} 화장터, ${district} 화장터, 
  ${regionText} 화장장 추천, ${sido} 화장장 추천, ${sigungu} 화장장 추천, ${district} 화장장 추천, 
  ${regionText} 화장장 정보, ${sido} 화장장 정보, ${sigungu} 화장장 정보, ${district} 화장장 정보, 
  ${regionText} 화장장 비교, ${sido} 화장장 비교, ${sigungu} 화장장 비교, ${district} 화장장 비교, 
  ${regionText} 화장장 예약, ${sido} 화장장 예약, ${sigungu} 화장장 예약, ${district} 화장장 예약,
  ${regionText} 화장장 비용, ${sido} 화장장 비용, ${sigungu} 화장장 비용, ${district} 화장장 비용,
  ${regionText} 화장터 추천, ${sido} 화장터 추천, ${sigungu} 화장터 추천, ${district} 화장터 추천, 
  ${regionText} 화장터 정보, ${sido} 화장터 정보, ${sigungu} 화장터 정보, ${district} 화장터 정보, 
  ${regionText} 화장터 비교, ${sido} 화장터 비교, ${sigungu} 화장터 비교, ${district} 화장터 비교, 
  ${regionText} 화장터 예약, ${sido} 화장터 예약, ${sigungu} 화장터 예약, ${district} 화장터 예약,
  ${regionText} 화장터 비용, ${sido} 화장터 비용, ${sigungu} 화장터 비용, ${district} 화장터 비용,
  ${sido} 화장예약, ${sido} 화장 비용, 
  ${sigungu} 화장예약, ${sigungu} 화장 비용,
  ${regionText} 화장예약, ${regionText} 화장 비용,
  ${district} 화장예약, ${district} 화장 비용,
  ${regionCrematoriums
    ?.map((c) => {
      const name =
        c.companyname.includes("화장장") || c.companyname.includes("화장터")
          ? c.companyname
          : `${c.companyname} 화장장`;
      return `${name}, ${name} 화장비용, ${name} 화장예약, ${name} 서류, ${name} 시간, ${name} 시설, ${name} 유택동산, ${name} 근처 화장장, ${name} 근처 화장터`;
    })
    .join(", ")}, 화장장, 화장터, 화장예약, 화장 비용, 화장지원금`;

  return {
    props: {
      sidoName: sido,
      sigunguName: sigungu,
      district,
      regionCrematoriums,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage: regionCrematoriums[0]?.images[0]?.url,
    },
  };
};

function CrematoriumRegionSigunguPage({
  sidoName,
  sigunguName,
  district,
  regionCrematoriums,
  pageOgTitle,
}: {
  sidoName: string;
  sigunguName: string;
  district: string;
  regionCrematoriums: CrematoriumDetail[];
  pageOgTitle: string;
}) {
  const isChuseokCallOff = useIsChuseokCallOff();
  const defaultParlor = regionCrematoriums[0];
  const totalCrematoriums = regionCrematoriums.length;

  const regionText = `${sidoName} ${district}`;

  const crematoriumPageSchema = buildCrematoriumPageSchema(regionCrematoriums, {
    regionText,
    goiWebUrl: GOI_WEB_URL,
  });

  const CrematoriumRegionHeroSection = () => {
    return (
      <VStack
        width="100%"
        style={{
          background: regionColor.c100,
        }}
      >
        <img
          src={defaultParlor?.images[0]?.url}
          alt={`${defaultParlor?.companyname} 시설 사진`}
          width="100%"
          height="100%"
        />
      </VStack>
    );
  };

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
      </Head>
      <RichSearch data={crematoriumPageSchema} />

      <main>
        <section style={{ padding: "20px 20px" }}>
          <Box margin="0px 0px 20px 0px">
            <CrematoriumRegionHeroSection />
          </Box>

          <h1 className="head_24_b">
            {regionText} 화장장, 화장터 시설 정보(비용, 예약, 서류, 혜택)
          </h1>
          <p
            className="body_14_m gray700"
            style={{
              marginBottom: "5px",
            }}
          >
            {regionText} 및 근처의 화장장, 화장터 시설 정보(비용, 예약, 서류,
            혜택)를 확인해보세요.
          </p>

          <HLine height="1px" color={colors.gray100} />
        </section>

        <section
          style={{
            background: regionColor.c100,
            padding: "20px 34px",
          }}
        >
          <VStack>
            <p
              className="body_16_m gray900"
              style={{
                marginBottom: "20px",
              }}
            >
              아래 내용은 {sigunguName} 지역 주소지 기준으로 주로 이용하시는{" "}
              {district} 지역의 화장장의 정보(비용, 예약, 서류, 혜택)에요
              <br />
              <br />
              화장장을 알아보신다면 주소지 기준의 관내 화장장 비용 적용 여부를
              확인해보실 필요가 있어요.
            </p>

            {/* 2026 추석. 연휴에는 못 받는 전화라 이 버튼만 내린다. */}
            {!isChuseokCallOff && (
              <Button
                backgroundColor={regionColor.c400}
                border="none"
                width="100%"
                borderRadius="8px"
                padding="16px"
                cursor="pointer"
                onClick={() => {
                  customEvent({
                    domain: "ch",
                    path: "crematorium",
                    trigger_and_target: "goi_call",
                  });

                  call("1666-9784");
                }}
              >
                <VStack gap="0px">
                  <Box className="caption_12_m white">
                    화장장 비용 및 예약이 필요하신 상황인가요?
                  </Box>
                  <Box className="subtitle_16_b white">
                    화장장 비용 및 예약 알아보기
                  </Box>
                </VStack>
              </Button>
            )}
          </VStack>
        </section>

        <section style={{ padding: "20px 16px", marginTop: "20px" }}>
          <VStack
            width="100%"
            gap="5px"
            alignItems="flex-start"
            padding="0px 0px 10px 0px"
          >
            <h2 className="subtitle_16_b gray900">{regionText} 화장장 목록</h2>
            <p className="body_14_m gray700">
              {totalCrematoriums}개의 화장장이 존재해요. ({regionText} 인접 지역
              포함)
            </p>
          </VStack>

          <VStack as="ul" width="100%" gap="16px">
            {regionCrematoriums.map((crematorium) => (
              <CrematoriumItemCard
                key={crematorium.id}
                crematorium={crematorium}
              />
            ))}
          </VStack>
        </section>

        <CrematoriumRegionFAQ />
      </main>
    </>
  );
}

export default CrematoriumRegionSigunguPage;

const CrematoriumItemCard = ({
  crematorium,
}: {
  crematorium: CrematoriumDetail;
}) => {
  const onClickEvent = () => {
    customEvent({
      domain: "ch",
      path: "crematorium",
      trigger_and_target: "item_click",
    });
  };

  return (
    <VStack
      as="li"
      width="100%"
      gap="0px"
      overflow="hidden"
      border={`1px solid ${colors.gray100}`}
    >
      <a
        href={`${GOI_WEB_URL}/crematorium-recommendation/${crematorium?.id}/?utm_source=dogsound`}
        style={{
          width: "100%",
        }}
        onClick={onClickEvent}
      >
        <Box
          sx={{
            width: "100%",
            height: "180px",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <img
            width="100%"
            height="100%"
            style={{
              objectFit: "cover",
            }}
            src={crematorium?.images[0]?.url}
            alt={`${crematorium?.companyname} 화장장 시설 모습`}
          />
        </Box>
      </a>

      <VStack width="100%" padding="16px">
        <VStack width="100%" alignItems="flex-start" gap="0px">
          <h2 className="subtitle_16_b gray900">{crematorium?.companyname}</h2>
          <Box className="body_14_m gray900">{crematorium?.address}</Box>
        </VStack>
        <HStack width="100%" alignItems="center" gap="4px">
          <Box className="body_14_m gray900">자세히 보기</Box>
          <ColorIcon name="arrow_right_small" color={colors.gray900} />
        </HStack>
      </VStack>
    </VStack>
  );
};
